// Plan-tier entitlements — single source of truth for feature gating.
//
// The shop record has no typed "planTier" field: ShopSettings (schema.graphql)
// stores everything as one settingsJson AWSJSON blob, and the tier already
// lives at settings.subscription.plan ('flow' | 'ops' | 'full'), written by
// the Stripe webhook (api/stripe-webhook.js). This module reads that value —
// it does not introduce a second, competing source of truth.
//
// No subscription record at all (subscription === null, or no .status) means
// a legacy/grandfathered shop: treated as FULL so nothing disappears.

const TIER_RANK = { flow: 0, ops: 1, full: 2 };

// feature -> minimum tier required
export const FEATURES = {
  board:            'flow',
  schedule:         'ops',
  customers:        'ops',
  analytics:        'ops',
  'labor-capture':  'ops',
  'status-texts':   'ops',
  'approval-texts': 'ops',
  estimates:        'full',
  invoicing:        'full',
  collections:      'full',
  'qbo-export':     'full',
  reports:          'full',
};

// The shop's plan tier as a plain string: 'flow' | 'ops' | 'full'. A shop
// with no subscription record is legacy/grandfathered and treated as full;
// a canceled sub drops to the base 'flow' shape (nothing is deleted, money
// features just stop rendering).
export function planTier(shop) {
  const sub = shop?.subscription;
  if (!sub || !sub.status) return 'full';
  if (sub.status === 'canceled') return 'flow';
  return String(sub.plan || 'flow').toLowerCase();
}

// shop = the shop's settings object (useShopSettings' `settings`, or an
// equivalent parsed settingsJson on the server).
export function hasFeature(shop, feature) {
  const sub = shop?.subscription;
  if (!sub || !sub.status) return true; // legacy/grandfathered
  if (sub.status === 'canceled') return false;

  const minTier = FEATURES[feature];
  if (!minTier) return false; // unknown feature — fail closed

  const tier = String(sub.plan || 'flow').toLowerCase();
  return (TIER_RANK[tier] ?? 0) >= TIER_RANK[minTier];
}

// Road Service is included in Ops and Full — that is what the pricing page
// sells today ("Dispatch & Road Service" on the Ops card in BillingView).
// It is ALSO sellable as a standalone add-on at any tier via
// settings.subscription.roadServiceSku, which is how a Flow-tier or
// road-only account gets it once Phase 3 makes the SKU purchasable.
//
// Checking the flag alone was a fail-closed bug: nothing writes
// roadServiceSku, so any shop with a Stripe subscription record lost the
// Road workspace entirely even on the tier that includes it.
//
// Same legacy rule as hasFeature: a shop with no subscription record keeps
// everything it has today (the pilot shop uses Road Service and must not
// lose it when the nav gates on this).
export function hasRoadService(shop) {
  const sub = shop?.subscription;
  if (!sub || !sub.status) return true; // legacy/grandfathered
  if (sub.roadServiceSku) return true;  // standalone add-on, any tier
  if (sub.status === 'canceled') return false;
  const tier = String(sub.plan || 'flow').toLowerCase();
  return (TIER_RANK[tier] ?? 0) >= TIER_RANK.ops;
}
